import React, { MouseEventHandler, useEffect, useState, useMemo } from "react";
import Badge from "../../../components/Badge";
import Button from "../../../components/Button";
import Loader from "../../../components/Loader";
import H4 from "../../../components/Typography/H4";
import { useFetchApi } from "../../../hooks/useFetchApi";
import { Outlet, useNavigate } from "react-router-dom";
import routes from "../../../setup/routes";
import DeleteModal from "./DeleteModal";
import { Table } from "../../../components/table";
import type { ColumnsType } from "antd/es/table";

// Define the Role type
interface Role {
  id: number;
  name: string;
  status: boolean;
}

const Roles: React.FC = (): React.ReactElement => {
  const {
    execute,
    loading,
    response: roles,
  } = useFetchApi("/api/roles", {}, "GET");
  const navigate = useNavigate();
  const [deleteData, setDeleteData] = useState<any>({});
  const [showDeleteModal, toggleDeleteModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const toggle = (e: any, role: any) => {
    navigate(
      `${routes.DASHBOARD}/${routes.ROLES}/${routes.ROLE_PERMISSIONS.replace(
        ":id",
        role.id
      )}`
    );
  };

  const toggleNew = (e: any, role: any) => {
    navigate(
      `${routes.DASHBOARD}/${routes.ROLES}/${routes.NEW_ROLE_PERMISSIONS}`
    );
  };

  const handleDelete = (e: any, role: any) => {
    setDeleteData({ id: role.id });
    toggleDeleteModal(true);
  };

  const filteredRoles = useMemo(() => {
    if (!roles || !searchTerm.trim()) {
      return roles || [];
    }
    return roles.filter((role: Role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [roles, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const columns: ColumnsType<Role> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "left",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status: boolean) => (
        <Badge status={status ? "success" : "error"}>
          {status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 450,
      render: (_, role: Role) => (
        <div className="flex gap-2 justify-center">
          <Button
            fontSize="small"
            variant="secondary"
            id="view"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, role)
            }
          >
            View Permissions
          </Button>
          <Button
            fontSize="small"
            variant="primary"
            id="newEdit"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, role)
            }
          >
            Edit
          </Button>
          <Button
            fontSize="small"
            variant="error"
            id="delete"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              handleDelete(e, role)
            }
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    execute();
  }, []);

  return (
    <div className="flex flex-col w-full p-8 bg-white min-h-full">
      <Loader show={loading} />
      <DeleteModal
        id="delete"
        data={deleteData}
        status={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSuccess={execute}
      />

      <div className="flex justify-between mb-8 items-center flex-wrap gap-4">
        {/* Left side - Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-64 max-w-md">
            <input
              type="text"
              placeholder="Search role name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          )}
          {searchTerm && (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              Found {filteredRoles.length} of {roles?.length || 0}
            </span>
          )}
        </div>

        {/* Right side - Action Button */}
        <Button id="newEdit" onClick={toggleNew} variant="primary">
          Add New Roles
        </Button>
      </div>

      <div className="relative z-0">
        <Table<Role>
          columns={columns}
          dataSource={filteredRoles}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          nullText="No data available. Add new"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default Roles;
